import {
    Button, Image, Overflow,
    OverflowItem,
    OverflowItemProps,
    useIsOverflowItemVisible,
    useOverflowMenu, MenuItem, Menu, MenuTrigger, MenuButton, MenuPopover, MenuList
} from '@fluentui/react-components';
import {
    ChevronLeftRegular,
    ChevronRightRegular,
} from "@fluentui/react-icons";
import React, {useEffect, useState} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    useGetBundlesQuery, useGetCategoriesQuery,
    useGetFeaturedProductsQuery,
    useDismissRecommendationMutation,
    useGetStorefrontQuery
} from '../Api/Api';
import FeaturedProductDisplay from '../Components/FeaturedProducts/FeaturedProductsDisplay';
import FeaturedBundlesDisplay from '../Components/FeaturedProducts/FeaturedBundlesDisplay';
import {IStorefrontBanner} from "../Api/Models/StorefrontSettings";
import {IProductCategory, IStorefrontSection} from "../Api/Models/Products";
import {isDarkColor} from "../Components/Helpers/ColorHelpers";
import { bannerRotationMs, hasRenderableBanner, hasZeroLeftInset, INavPodFilter, EDGE_MIN_LEFT_INSET, navPodAttributes, navPodJustify, navPodScrollJustify, parseHomePageConfig, svgMaskStyle } from "../Components/Helpers/homePageConfig";
import { useSideScroll } from "../Components/Helpers/useSideScroll";
import { cardStyleFrom, containerStyleFrom, elementStyleFrom, importantStyleCss, priceStyleFrom, textStyleFrom } from "../Components/Helpers/searchPageConfig";
import BannerOverlayCta from '../Components/Sections/BannerOverlayCta';
import { overlayBoxStyleFrom, overlayContentStyleFrom, overlayFrameStyleFrom, overlayFramedLayerStyle, overlayLayerStyleFrom, overlayScaledFrameStyle, parseBannerOverlayConfig } from "../Components/Helpers/bannerOverlayConfig";
import { hoverPartFor, resolveHoverPart, resolveSectionOverride, resolveStyleProps } from "../Components/Helpers/stylesConfig";
import SearchBox from '../Components/Search/SearchBox';
import { parseAutocompleteConfig } from '../Components/Search/autocompleteConfig';
import { useLocalization } from "../Components/Helpers/useLocalization";
import { useIsNarrow } from "../Components/Helpers/useIsNarrow";
import { findPageBySlug, IStorefrontCustomPage } from "../Components/Helpers/pagesConfig";
import { isContentSection } from "../Components/Helpers/contentSectionConfig";
import ContentSection from "../Components/Sections/ContentSection";
import { renderSectionCells } from "../Components/Sections/SectionCells";
import SectionRowLayout from "../Components/Sections/SectionRowLayout";
import NotFoundPage from "./NotFoundPage";

const OverflowMenuItem: React.FC<Pick<OverflowItemProps, "id">> = (props) => {
    const { id } = props;
    const isVisible = useIsOverflowItemVisible(id);
    const categories = useGetCategoriesQuery();
    const [selected, setSelected] = useState<IProductCategory>();
    const nav = useNavigate();

    useEffect(() => {
        if (categories.data) {
            const cat = categories.data.find(a => a.id === id);
            if (cat) {
                setSelected(cat);
            }
        }
    }, [categories.data, id]);
    if (isVisible) {
        return null;
    }
    
    return (<React.Fragment>{selected ?
        <MenuItem
            icon={selected.smallImageUrl
                ? <img src={selected.smallImageUrl} alt='' style={{ width: '20px', height: '20px', objectFit: 'contain' }} />
                : undefined}
            onClick={() => {
            if (selected.identifier) {
                nav(`/search?category=${encodeURIComponent(selected.identifier)}`);
            }}}
        >
            {selected.label}
        </MenuItem> : <></>}
        </React.Fragment>
    );
};

const OverflowMenu: React.FC<{ itemIds: string[] }> = ({ itemIds }) => {
    const { ref, overflowCount, isOverflowing } =
        useOverflowMenu<HTMLButtonElement>();
    const settings = useGetStorefrontQuery();
    const isDark = isDarkColor(settings.data?.backgroundColor);

    if (!isOverflowing) {
        return null;
    }

    return (
        <Menu>
            <MenuTrigger disableButtonEnhancement>
                <MenuButton ref={ref} className='category-pod-button'>+{overflowCount} items</MenuButton>
            </MenuTrigger>

            <MenuPopover className={isDark ? 'sf-menu-popover-dark' : undefined}>
                <MenuList>
                    {itemIds.map((i) => {
                        return <OverflowMenuItem key={i} id={i} />;
                    })}
                </MenuList>
            </MenuPopover>
        </Menu>
    );
};

const HomePage: React.FC = () => {
    const settings = useGetStorefrontQuery();
    // Custom-page context: HomePage backs both '' / '/home' (main homepage, no slug) and the
    // dynamic '/:pageSlug' route. pageSlug is undefined on the main homepage; on a custom page it's
    // resolved to an enabled page via findPageBySlug (unknown/disabled → customPage undefined → 404
    // below). Sections are page-filtered by the API via the featured query's `page` arg; banners are
    // filtered client-side (see the validBanners effect).
    const { pageSlug } = useParams<{ pageSlug?: string }>();
    const customPage: IStorefrontCustomPage | undefined = React.useMemo(
        () => (pageSlug ? findPageBySlug(settings.data, pageSlug) : undefined),
        [settings.data, pageSlug]);
    // A slug that doesn't resolve to an enabled page is a 404. Only decide once settings have loaded
    // (Layout blocks rendering until then, but guard anyway so a refetch can't flash a false 404).
    const isUnknownPage = !!pageSlug && !!settings.data && !customPage;
    const featuredProducts = useGetFeaturedProductsQuery({ page: customPage?.id });
    // Dismissing a "Recommended For You" card. The page id is handed to the mutation so its
    // optimistic patch targets this page's featured cache entry, not the homepage's.
    const [dismissRecommendation] = useDismissRecommendationMutation();
    const dismissRecommendedProduct = React.useCallback(
        (productId: string) => { dismissRecommendation({ productId, page: customPage?.id }); },
        [dismissRecommendation, customPage?.id]
    );
    const bundles = useGetBundlesQuery()
    const categories = useGetCategoriesQuery()
    const [keywords, setKeywords] = React.useState<string>('');
    const nav = useNavigate();
    const L = useLocalization();
    const [currentBannerIndex, setCurrentBannerIndex] = React.useState(0);
    const [selfToggledBanner, setSelfToggledBanner] = useState(false);
    const [validBanners, setValidBanners] = useState<IStorefrontBanner[]>([]);
    const [minimizedSections, setMinimizedSections] = useState<Set<number>>(new Set());
    // On phones, thin the (possibly very wide) LCARS section rail and go single-column so a
    // card can use the full width instead of being crowded against the border.
    const isNarrow = useIsNarrow(700);

    // On a custom page, set the browser tab title to the page's title and restore the previous title
    // on unmount / navigation away. The app doesn't otherwise manage document.title, so this is a
    // self-contained effect; the main homepage (no customPage) leaves the title untouched.
    useEffect(() => {
        if (!customPage?.title) return;
        const previous = document.title;
        document.title = customPage.title;
        return () => { document.title = previous; };
    }, [customPage?.title]);

    // Per-page banner height overrides the storefront-wide bannerHeight when set (custom pages only).
    const effectiveBannerHeight = customPage?.bannerHeight ?? settings.data?.bannerHeight;

    // Home page presentation config (homePageConfig). Strictly additive: an empty config
    // makes every style below undefined, so cards and category pods render exactly as they
    // do today. Reuses the search config builders so the live page matches the admin preview.
    const homeConfig = parseHomePageConfig(settings.data?.homePageConfig);
    // Memoized because SuggestingSearchBox keys its own memos (resolved styles, requested columns) on
    // this object's identity — re-parsing the blob inline would hand it a new object every render and
    // rebuild all of them. Same treatment NavBar gives the header blob.
    const homeAutocomplete = React.useMemo(
        () => parseAutocompleteConfig(settings.data?.homePageConfig), [settings.data?.homePageConfig]);
    const orUndef = (s: React.CSSProperties): React.CSSProperties | undefined => (Object.keys(s).length ? s : undefined);
    const homePageTitleStyle = orUndef(textStyleFrom(homeConfig.pageTitle, 'heading'));
    const homeBannerTextStyle = orUndef(textStyleFrom(homeConfig.bannerText));
    const homeBannerStyle = orUndef(elementStyleFrom(homeConfig.banner));
    const homeAddToCartStyle = orUndef(elementStyleFrom(homeConfig.addToCartButton));
    // Side-scroll section chrome (see IHomePageConfig.scrollButton/scrollbar).
    // Section collapse/expand control. The style is merged OVER the built-in look so a merchant can
    // change just the color (or just the offset) without restating the rest; the hover part is a
    // scoped rule keyed off the HEADER's hover, because the whole band is the toggle.
    const homeSectionToggleStyle = orUndef(elementStyleFrom(homeConfig.sectionToggle));
    // The title TEXT block inside the header band (homePageConfig.sectionTitleText). Emitted as a
    // scoped rule rather than an inline style because the element it targets is rendered deep inside
    // the section markup. Colour is deliberately NOT set here: it inherits from the band's own
    // titleStyleProps, which is per-section, so one page-wide rule can give every section the same
    // knocked-out block while each keeps its own text tint.
    const homeSectionTitleTextCss = React.useMemo(() => {
        const s = elementStyleFrom(homeConfig.sectionTitleText);
        return Object.keys(s).length ? importantStyleCss(s) : undefined;
    }, [homeConfig.sectionTitleText]);
    const homeSectionToggleHoverCss = React.useMemo(() => {
        const s = elementStyleFrom(resolveHoverPart(homeConfig.sectionToggleHover, homeConfig.sectionToggle), 'hover');
        return Object.keys(s).length ? importantStyleCss(s) : undefined;
    }, [homeConfig.sectionToggleHover, homeConfig.sectionToggle]);
    // U+2212 MINUS SIGN, not a hyphen: it matches the '+' visually at the same optical weight.
    const sectionExpandIcon = homeConfig.sectionToggleExpandIcon || '+';
    const sectionCollapseIcon = homeConfig.sectionToggleCollapseIcon || '−';
    const homeScrollButtonStyle = orUndef(elementStyleFrom(homeConfig.scrollButton));
    const homeScrollbarStyle = orUndef(elementStyleFrom(homeConfig.scrollbar));
    const homeMoreLikeThisStyle = orUndef(elementStyleFrom(homeConfig.moreLikeThisButton));
    const homeCardStyle = orUndef(cardStyleFrom(homeConfig.card));
    const homeNameStyle = orUndef(textStyleFrom(homeConfig.title, 'title'));
    const homePriceStyle = orUndef(priceStyleFrom(homeConfig.price));
    const homeImageStyle = orUndef(elementStyleFrom(homeConfig.image, 'image'));
    const homePodStyle = orUndef(elementStyleFrom(homeConfig.pod));
    const homePodNameStyle = orUndef(textStyleFrom(homeConfig.podName));
    const homePodContainerStyle = orUndef(containerStyleFrom(homeConfig.podContainer));
    // Pods are wrapped in Fluent's <OverflowItem>, whose merged ref swallows a child ref
    // callback — so the ref-based importantStyle approach never runs on them. Inject the
    // pod style as a scoped <style> rule (with !important, to beat the theme's forced
    // .category-pod-button background) instead. See getCategoryPods below.
    const homePodCss = homePodStyle ? importantStyleCss(homePodStyle) : undefined;
    // Nav pods reuse the same style machinery as category pods. The pod button style is
    // injected as a scoped <style> with !important (importantStyleCss) because the theme
    // CSS may force button backgrounds/borders that a plain inline style can't beat.
    const homeNavPodStyle = orUndef(elementStyleFrom(homeConfig.navPod));
    const homeNavPodNameStyle = orUndef(textStyleFrom(homeConfig.navPodName));
    const homeNavPodImageStyle = orUndef(elementStyleFrom(homeConfig.navPodImage));
    const homeNavPodLinkStyle = orUndef(textStyleFrom(homeConfig.navPodLink));
    const homeNavPodContainerStyle = orUndef(containerStyleFrom(homeConfig.navPodContainer));
    // Only used in scroll mode, but hooks cannot be called conditionally.
    const navPodScroll = useSideScroll('.nav-pod-button', 14, [homeConfig.navPods]);
    const homeNavPodCss = homeNavPodStyle ? importantStyleCss(homeNavPodStyle) : undefined;
    // Hover styling for the home page's interactable tiles — product cards, category pods,
    // and nav pods. `:hover` can't be an inline style, so each is emitted as a scoped
    // stylesheet rule (with !important, to beat the theme's forced element rules) plus a
    // short transition so the change animates.
    // Each hover also picks up the `hover` part of the named style its RESTING element references
    // (resolveHoverPart's second argument), merged UNDER the local hover dict. With no `$styleRef`
    // and no local hover dict the result is undefined and no rule is emitted, exactly as before.
    const homeCardHoverCss = React.useMemo(() => {
        const s = elementStyleFrom(resolveHoverPart(homeConfig.cardHover, homeConfig.card), 'hover'); return Object.keys(s).length ? importantStyleCss(s) : undefined;
    }, [homeConfig.cardHover, homeConfig.card]);
    // The same resolved hover, handed to the card as a style object rather than a CSS rule. The
    // `.card:hover` rule below still carries the transition and covers cards that take no cardStyle;
    // this is what actually repaints a card whose resting style is applied inline-!important, which
    // no stylesheet rule can override. See ProductCard's cardHoverStyle prop.
    const homeCardHoverStyle = React.useMemo(
        () => orUndef(elementStyleFrom(resolveHoverPart(homeConfig.cardHover, homeConfig.card), 'hover')),
        [homeConfig.cardHover, homeConfig.card]);
    const homePodHoverCss = React.useMemo(() => {
        const s = elementStyleFrom(resolveHoverPart(homeConfig.podHover, homeConfig.pod), 'hover'); return Object.keys(s).length ? importantStyleCss(s) : undefined;
    }, [homeConfig.podHover, homeConfig.pod]);
    const homeNavPodHoverCss = React.useMemo(() => {
        const s = elementStyleFrom(resolveHoverPart(homeConfig.navPodHover, homeConfig.navPod), 'hover'); return Object.keys(s).length ? importantStyleCss(s) : undefined;
    }, [homeConfig.navPodHover, homeConfig.navPod]);
    // Hover for the floating add-to-cart button on product cards — the `hover` part of the button
    // style the button references (there is no local per-page add-to-cart hover config today).
    const homeAddToCartHoverCss = React.useMemo(() => {
        const s = elementStyleFrom(hoverPartFor(homeConfig.addToCartButton), 'hover'); return Object.keys(s).length ? importantStyleCss(s) : undefined;
    }, [homeConfig.addToCartButton]);

    // Uniform inter-block spacing (homeConfig.homeContentPaddingTop / homeBlockGap — see
    // IHomePageConfig). Both strictly opt-in: undefined leaves every margin/padding below exactly
    // as it renders today.
    const homeContentPaddingTop = homeConfig.homeContentPaddingTop;
    const homeBlockGap = homeConfig.homeBlockGap;

    // Hero source: the five hero fields (bannerFullWidth / bannerImageFit / bannerUnderHeader /
    // bannerUnderHeaderBackground / bannerUnderHeaderShadow) resolve per context — the custom page's
    // own entry on a custom-page route, else homeConfig on the main homepage. Mirrors
    // effectiveBannerHeight above: the per-page value wins on a custom page, homeConfig drives the
    // main homepage. Layout re-derives the same source for the header/#content chrome (see there);
    // both sides read from the identical fields so they stay in lockstep. All strictly opt-in.
    const heroBannerFullWidth = customPage ? customPage.bannerFullWidth : homeConfig.bannerFullWidth;
    const heroBannerImageFit = customPage ? customPage.bannerImageFit : homeConfig.bannerImageFit;
    const heroBannerUnderHeader = customPage ? customPage.bannerUnderHeader : homeConfig.bannerUnderHeader;

    // Full-bleed bands (bannerFullWidth / navPodBand / sectionBands — see IHomePageConfig). The
    // full-bleed hero now applies on BOTH the main homepage (homeConfig.bannerFullWidth) and a custom
    // page that opts in via its own bannerFullWidth. A banner SECTION's own fullBleed flag is a
    // separate way to full-bleed a section within either page.
    const homeBannerFullWidth = !!heroBannerFullWidth;
    // Size a full-bleed carousel to the image's natural height (no fixed-height cover crop). Only
    // meaningful for full-bleed; safe when banners share an aspect ratio. See IHomePageConfig.bannerAutoHeight.
    const homeBannerAutoHeight = homeBannerFullWidth && !!homeConfig.bannerAutoHeight;
    // Carousel dots below the hero instead of over it (IHomePageConfig.bannerDotsBelow). Opt-in:
    // the shipped placement is the overlay, and this moves a visible control on a shared runtime.
    const dotsBelowBanner = homeConfig.bannerDotsBelow === true;
    // Scale the banner overlay text/CTA WITH the banner (container-query units) rather than locking
    // it to a fixed pixel maxWidth. See IHomePageConfig.bannerOverlayScale.
    const homeOverlayScale = homeBannerFullWidth && !!homeConfig.bannerOverlayScale;
    // Resolved object-fit for the fixed-height banner image. An explicit Image fit setting wins
    // ('fill' → cover / crop, 'fit' → contain / letterbox); otherwise fall back to the
    // bannerFullWidth heuristic (full-bleed → cover, otherwise contain).
    const resolvedObjectFit: React.CSSProperties['objectFit'] = heroBannerImageFit
        ? (heroBannerImageFit === 'fill' ? 'cover' : 'contain')
        : (homeBannerFullWidth ? 'cover' : 'contain');
    // "Hero under transparent header" (bannerUnderHeader): the activation test here is the config +
    // desktop + renderable-banner checks (Layout adds the route check on its side). On a custom page
    // the renderable-banner check is scoped to that page's banners (pageId). When active, Layout drops
    // #content's navbar clearance and the header floats over the hero; we mirror that by zeroing the
    // top gap above the banner below so the full-bleed hero sits flush at the very top of #content.
    const underHeaderActive = heroBannerUnderHeader === true
        && homeBannerFullWidth
        && !isNarrow
        && hasRenderableBanner(settings.data, customPage?.id);
    const homeNavPodBandStyle = orUndef(elementStyleFrom(homeConfig.navPodBand));
    const homeSectionBandStyles = React.useMemo(
        () => (homeConfig.sectionBands ?? []).map(b => { const s = elementStyleFrom(b); return Object.keys(s).length ? s : undefined; }),
        [homeConfig.sectionBands]);

    // Affiliations / endorsements / awards band (homeConfig.affiliations — see IHomePageConfig).
    // Strictly opt-in: no config, or a config with no items carrying an imageUrl, renders nothing.
    const homeAffiliations = homeConfig.affiliations;
    const homeAffiliationsBandStyle = orUndef(elementStyleFrom(homeAffiliations?.band));
    const homeAffiliationsPodStyle = orUndef(elementStyleFrom(homeAffiliations?.pod));
    const homeAffiliationsImageStyle = orUndef(elementStyleFrom(homeAffiliations?.image));
    const homeAffiliationsLabelStyle = orUndef(textStyleFrom(homeAffiliations?.labelStyle));
    const homeAffiliationsDescriptionStyle = orUndef(textStyleFrom(homeAffiliations?.descriptionStyle));

    const homeMaxWidth = settings.data?.maxWidth ? `${settings.data.maxWidth}px` : '800px';
    // Break a row out of the max-width column to span the full viewport. Safe because the layout
    // ancestors (#content, html/body/#root — see Layout.tsx) use overflow-x: clip, so 100vw adds no
    // horizontal scrollbar. constrainInner (default) re-centers the inner content at maxWidth; pass
    // false to let the child fill the full width (used for a full-bleed banner image).
    const fullBleedBand = (bandStyle: React.CSSProperties | undefined, children: React.ReactNode, opts?: { key?: React.Key; constrainInner?: boolean }): React.ReactNode => (
        <div key={opts?.key} style={{ width: '100vw', marginLeft: 'calc(50% - 50vw)', boxSizing: 'border-box', ...bandStyle }}>
            {opts?.constrainInner === false
                ? children
                : <div style={{ maxWidth: homeMaxWidth, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>{children}</div>}
        </div>
    );

    const toggleSection = (idx: number) => {
        setMinimizedSections(prev => {
            const next = new Set(prev);
            if (next.has(idx)) next.delete(idx); else next.add(idx);
            return next;
        });
    };
    useEffect(() => {
        if (settings.data?.storefrontBanners) {
            const pageId = customPage?.id;
            const seen = new Set<string>();
            const valid = settings.data.storefrontBanners.filter(banner => {
                // Page scoping: the main homepage (no custom page) shows only un-scoped banners
                // (a truthy `page` belongs to a custom page); a custom page shows only its own.
                if (pageId) {
                    if (banner.page !== pageId) return false;
                } else if (banner.page) {
                    return false;
                }
                if (!banner.imageUrl || banner.imageUrl.trim() === '') return false;
                if (seen.has(banner.imageUrl)) return false;
                seen.add(banner.imageUrl);
                return true;
            });
            setValidBanners(valid);
            // Switching pages (or a banners refresh) rebuilds the list — reset the carousel so a stale
            // index can't point past the new set (blank banner) and auto-advance resumes from the top.
            setCurrentBannerIndex(0);
            setSelfToggledBanner(false);
        } else {
            setValidBanners([]);
            setCurrentBannerIndex(0);
        }
    }, [settings.data?.storefrontBanners, customPage?.id]);

    // Dwell time per slide. Defaults to the 7s that used to be hardcoded here; a storefront can
    // lengthen it for a slide whose artwork is timed (an animated banner that runs past 7s would
    // otherwise be cut off mid-play), or set 0 to leave the dots as the only way to advance.
    const bannerRotateMs = bannerRotationMs(homeConfig);
    useEffect(() => {
        if (!validBanners.length || bannerRotateMs === null) return;
        const interval = setInterval(() => {
            if(!selfToggledBanner) {
                setCurrentBannerIndex(prev =>
                    prev < validBanners.length - 1 ? prev + 1 : 0
                );
            }
        }, bannerRotateMs);
        return () => clearInterval(interval);
    }, [validBanners, selfToggledBanner, bannerRotateMs]);

    // Merchant-supplied link navigation, shared by the banner (image click / overlay CTA) and
    // link-mode nav pods: external URLs open a new tab (noopener/noreferrer so the URL can't get
    // a handle on this window), internal paths navigate in-app.
    const navigateToBannerUrl = (url?: string) => {
        if (!url) return;
        if (url.startsWith('http')) {
            window.open(url, '_blank', 'noopener,noreferrer');
        } else {
            nav(url);
        }
    };

    const getBanner = () => {
        let currentBanner : IStorefrontBanner | undefined = undefined
        let useScroll = false;
        if (validBanners && validBanners.length > 0) {
            currentBanner = validBanners[currentBannerIndex];
            useScroll = validBanners.length > 1;
        }
        else if (!customPage) {
            // Legacy single-banner fallback is a MAIN-homepage concept only. A custom page with no
            // banners of its own renders no banner area (no global/legacy fallback).
            currentBanner = {imageUrl: settings.data?.storefrontBannerImage, altText: settings.data?.storefrontbannerAltText, linkUrl: settings.data?.storefrontBannerLink};
        }

        // A lone banner with no configured height keeps its natural aspect ratio (no height clamp,
        // image height:auto). A configured height sends it through the fixed-height branch below,
        // just like the multi-banner carousel and the legacy fallback banner.
        const useNaturalRatio = validBanners.length === 1 && !effectiveBannerHeight;

        const storefrontBg = settings.data?.backgroundColor;
        const fallbackBannerBg = storefrontBg
            ? (isDarkColor(storefrontBg) ? '#2a2a2a' : '#f0f0f0')
            : '#f0f0f0';

        const bannerHeight = effectiveBannerHeight ? `${effectiveBannerHeight}px` : '350px';

        const containerStyle: React.CSSProperties = useNaturalRatio
            ? {
                position: 'relative',
                display: 'flex',
                width: '100%',
                borderRadius: '8px',
                overflow: 'hidden',
                boxSizing: 'border-box'
            }
            : homeBannerAutoHeight
            ? {
                // Full-bleed carousel at the image's natural height: no fixed box, so the banner
                // scales with viewport width and object-fit never has to crop. Edge-to-edge (radius 0).
                position: 'relative',
                display: 'flex',
                width: '100%',
                borderRadius: 0,
                overflow: 'hidden',
                boxSizing: 'border-box'
            }
            : {
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: bannerHeight,
                minHeight: bannerHeight,
                maxHeight: bannerHeight,
                backgroundColor: (currentBanner?.backgroundColor ?? fallbackBannerBg),
                borderRadius: '8px',
                overflow: 'hidden',
                boxSizing: 'border-box'
            };

        const imageStyle: React.CSSProperties = useNaturalRatio
            ? {
                cursor: currentBanner?.linkUrl ? 'pointer' : 'default',
                width: '100%',
                height: 'auto',
                display: 'block',
                borderRadius: '8px'
            }
            : homeBannerAutoHeight
            ? {
                // Natural height: width fills, height follows the image aspect ratio (no crop).
                cursor: currentBanner?.linkUrl ? 'pointer' : 'default',
                width: '100%',
                height: 'auto',
                display: 'block'
            }
            : {
                cursor: currentBanner?.linkUrl ? 'pointer' : 'default',
                width: '100%',
                height: '100%',
                // How the image fills the fixed-height box. An explicit admin "Image fit" setting
                // (homeConfig.bannerImageFit) wins — 'fill' → cover, 'fit' → contain. With none set,
                // fall back to the bannerFullWidth heuristic: full-bleed (bannerFullWidth) boxes can
                // be much wider than the source image (up to 100vw), so 'contain' letterboxes it and
                // the container background shows through as gutters, whereas 'cover' fills the box,
                // cropping overflow instead; the non-full-bleed (max-width column) case, where the
                // box is much closer to the image's natural size, stays on 'contain'.
                objectFit: resolvedObjectFit,
                margin: 0,
                borderRadius: '8px',
                display: 'block'
            };

        // On phones the content column is edge-to-edge, so let the banner fill the full width and
        // scale the image to fill it instead of letterboxing inside a fixed-height box.
        const bannerContainerStyle: React.CSSProperties = isNarrow
            ? { ...homeBannerStyle, position: 'relative', display: 'flex', width: '100%', maxWidth: 'none', borderRadius: 0, overflow: 'hidden', boxSizing: 'border-box' }
            // containerType: inline-size makes the banner a query container so the overlay can size
            // itself in cqw (1% of banner width) and scale with the banner. Only when overlay-scale is on.
            : { ...containerStyle, ...homeBannerStyle, ...(homeOverlayScale ? { containerType: 'inline-size' } as React.CSSProperties : {}) };
        const bannerImageStyle: React.CSSProperties = isNarrow
            ? { cursor: currentBanner?.linkUrl ? 'pointer' : 'default', width: '100%', height: 'auto', display: 'block' }
            : imageStyle;

        // Dots overlay the bottom of the banner image itself (bannerContainerStyle is
        // position:relative), so use a fixed white-based contrast rather than picking from the
        // page background — imagery varies too much for one derived color to stay legible.
        // Moved BELOW the banner (bannerDotsBelow) they sit on the page instead, where white-on-white
        // would disappear on a light store, so derive them from the page text colour there.
        const dotOnPage = settings.data?.textColor || '#000000';
        const dotActiveColor = dotsBelowBanner ? dotOnPage : '#ffffff';
        const dotInactiveColor = dotsBelowBanner ? `${dotOnPage}66` : 'rgba(255,255,255,0.55)';

        // Per-banner overlay text/CTA (null for banners without a valid, non-empty config, so
        // existing banners render byte-identically).
        const overlay = currentBanner ? parseBannerOverlayConfig(currentBanner.overlayConfig) : null;

        // The overlay's inner content column (title/subtitle/CTA). Shared by both the normal and
        // the full-width (page-framed) wrappers below so the markup lives in one place.
        const overlayContent = overlay ? (
            <div style={{ ...overlayContentStyleFrom(overlay.position), ...overlayBoxStyleFrom(overlay) }}>
                {overlay.title?.trim() &&
                    <h2 style={{ margin: 0, fontSize: '2.5rem', fontWeight: 600, lineHeight: 1.2, color: '#1f2933', ...textStyleFrom(overlay.titleStyle, 'heading') }}>
                        {overlay.title}
                    </h2>
                }
                {overlay.subtitle?.trim() &&
                    <p style={{ margin: 0, fontSize: '1.1rem', color: '#1f2933', ...textStyleFrom(overlay.subtitleStyle, 'body') }}>
                        {overlay.subtitle}
                    </p>
                }
                {overlay.button?.label?.trim() &&
                    <BannerOverlayCta
                        button={overlay.button}
                        onActivate={() => navigateToBannerUrl(overlay.button?.url || currentBanner?.linkUrl)}
                    />
                }
            </div>
        ) : null;

        // A full-bleed banner re-frames the overlay to the page content width (centered) so its
        // text lines up with the rest of the page like a hero; otherwise the overlay fills the
        // banner directly (byte-identical to before).
        const overlayNode = overlay
            ? (homeBannerFullWidth
                ? (homeOverlayScale
                    ? (
                        // Overlay scales with the banner: the frame is sized in cqw so the CTA's
                        // position and size track the banner width (grows on wide/4K screens).
                        <div style={overlayFramedLayerStyle}>
                            <div style={overlayScaledFrameStyle(overlay.position)}>
                                {overlayContent}
                            </div>
                        </div>
                    )
                    : (
                        <div style={overlayFramedLayerStyle}>
                            <div style={overlayFrameStyleFrom(overlay.position, settings.data?.maxWidth ?? 800)}>
                                {overlayContent}
                            </div>
                        </div>
                    ))
                : (
                    <div style={overlayLayerStyleFrom(overlay.position)}>
                        {overlayContent}
                    </div>
                ))
            : null;

        // The carousel dot row, built once and placed by dotsBelowBanner: over the bottom of the hero
        // (as shipped) or in normal flow underneath it. The admin preview has always drawn it
        // underneath, so the two only agree once a store opts in — see IHomePageConfig.bannerDotsBelow.
        const bannerDotsNode = useScroll ? (
            <div
                className='banner-dots'
                style={dotsBelowBanner
                    // 28/14 rather than a bare 10: below the banner the row is page furniture separating
                    // two blocks, not a control floating on the hero, so it needs real air on both
                    // sides. Keep in step with the admin preview (StorefrontBannerEditor).
                    ? { display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '28px', marginBottom: '14px' }
                    : { position: 'absolute', bottom: '12px', left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: '8px' }}
            >
                {validBanners.map((_, i) => (
                    <button
                        key={i}
                        className='banner-dot'
                        aria-label={`Show banner ${i + 1}`}
                        aria-current={i === currentBannerIndex}
                        onClick={() => {
                            setSelfToggledBanner(true);
                            setCurrentBannerIndex(i);
                        }}
                        style={{
                            width: '11px',
                            height: '11px',
                            padding: 0,
                            borderRadius: '50%',
                            border: 'none',
                            cursor: 'pointer',
                            // Below the banner the dots sit on the PAGE, not on imagery, so the
                            // white-on-photo contrast pair would vanish on a light store. Fall back to
                            // the page's own text colour there and keep white over the hero.
                            background: i === currentBannerIndex ? dotActiveColor : dotInactiveColor,
                            transform: i === currentBannerIndex ? 'scale(1.2)' : 'none',
                            transition: 'background 0.2s, transform 0.2s',
                        }}
                    />
                ))}
            </div>
        ) : null;

        return(
            <React.Fragment>
                {currentBanner?.imageUrl &&
                    <div style={bannerContainerStyle}>
                        <Image
                            className='home-banner'
                            src={currentBanner?.imageUrl}
                            alt={currentBanner?.altText}
                            style={bannerImageStyle}
                            onClick={() => {
                                const banner = currentBanner;
                                if(banner) {
                                    if (banner.linkUrl) {
                                        if (banner.linkUrl.startsWith('http')) {
                                            // noopener/noreferrer: merchant-supplied URLs must not get a handle on this window.
                                            window.open(banner.linkUrl, '_blank', 'noopener,noreferrer');
                                        } else {
                                            nav(banner.linkUrl);
                                        }
                                    }
                                }
                            }}
                        />
                        {overlayNode}
                        {!dotsBelowBanner && bannerDotsNode}
                    </div>
                }
                {/* Below-the-banner placement has to live OUTSIDE the banner box: that box is a
                    fixed-height, overflow:hidden crop frame, so an in-flow row inside it is simply
                    clipped away. Rendered as a sibling instead, guarded on the banner existing so an
                    empty hero never leaves a stray dot row behind. */}
                {dotsBelowBanner && currentBanner?.imageUrl && bannerDotsNode}
            </React.Fragment>

        )
    };


    // "Browse All" is an explicit request to see everything, so it starts the search page from a clean
    // slate — state.fresh tells SearchPage to skip restoring the shopper's remembered filter set. The
    // remembered set is only skipped for this arrival, not forgotten (that's what "Clear filters" does).
    const showAll = () => {
        nav(`/search`, { state: { fresh: true } });
    }


    // Build the /search URL a nav pod navigates to from its filter. category/subcategory/segment/
    // manufacturer map to the same single-value params SearchPage reads; tags and attributes are
    // repeatable (tag=Value, facet=Key|Value — the exact formats SearchPage parses), so a pod can
    // land the shopper on a narrow slice: a segment + category + two tags + an attribute at once.
    const navPodSearchUrl = (filter?: INavPodFilter): string => {
        const params = new URLSearchParams();
        if (filter?.category) params.set('category', filter.category);
        if (filter?.subCategory) params.set('subcategory', filter.subCategory);
        if (filter?.segment) params.set('segment', filter.segment);
        if (filter?.manufacturer) params.set('manufacturer', filter.manufacturer);
        (filter?.tags ?? []).filter(t => !!t).forEach(t => params.append('tag', t));
        navPodAttributes(filter).forEach(a => params.append('facet', `${a.key}|${a.value}`));
        const qs = params.toString();
        return qs ? `/search?${qs}` : '/search';
    };

    const getNavPods = () => {
        const navPods = homeConfig.navPods;
        if (!navPods || navPods.length === 0) return null;
        // Scroll mode keeps the row strictly horizontal (nowrap + chevrons); otherwise pods wrap.
        const scroll = !!homeConfig.navPodScroll;
        const pods = (
            <div
                className={`nav-pod-container${scroll ? ' nav-pod-scroll' : ''}`}
                ref={scroll ? navPodScroll.ref : undefined}
                onScroll={scroll ? navPodScroll.checkScroll : undefined}
                // justifyContent first, so an explicit navPodContainer.justifyContent still wins.
                // homeBlockGap: zero the CSS class's own `margin: 1em 0` — the uniform gap comes
                // from the flex-column wrapper in the return below instead (avoids double-spacing
                // whether or not this row is also wrapped in a full-bleed navPodBand).
                style={{ justifyContent: scroll ? navPodScrollJustify(homeConfig.navPodAlign) : navPodJustify(homeConfig.navPodAlign), ...homeNavPodContainerStyle, ...(scroll ? homeScrollbarStyle : undefined), ...(homeBlockGap !== undefined ? { margin: 0 } : {}) }}
            >
                {homeNavPodCss && (
                    <style>{`.nav-pod-container .nav-pod-button { ${homeNavPodCss} }`}</style>
                )}
                {homeNavPodHoverCss && (
                    <style>{`.nav-pod-container .nav-pod-button { transition: box-shadow .18s ease, transform .18s ease, border-color .18s ease, background-color .18s ease; } .nav-pod-container .nav-pod-button:hover { ${homeNavPodHoverCss} }`}</style>
                )}
                {/* Nav-pod SVG icons are CSS masks, so their tint IS a background color. Emitted
                    AFTER the base .nav-pod-button rule deliberately: same specificity, same
                    !important, so the dedicated icon color wins over a background set on the
                    navPodImage dict — and !important is what beats the mask's inline
                    currentColor. */}
                {homeConfig.navPodIconColor && (
                    <style>{`.nav-pod-container .nav-pod-icon { ${importantStyleCss({ backgroundColor: homeConfig.navPodIconColor })} }`}</style>
                )}
                {homeConfig.navPodIconHoverColor && (
                    <style>{`.nav-pod-container .nav-pod-button:hover .nav-pod-icon { ${importantStyleCss({ backgroundColor: homeConfig.navPodIconHoverColor })} }`}</style>
                )}
                {navPods.map((pod) => (
                    <button
                        key={pod.id}
                        className="nav-pod-button"
                        // A pod with a linkUrl is a plain link (external → new tab, in-store path →
                        // in-app), and it wins over the filter; otherwise the filtered search.
                        // A pod is an explicit "take me to this slice" click, so it starts clean too
                        // (state.fresh) — an unfiltered pod is otherwise a bare /search and would
                        // pick up the remembered filters.
                        onClick={() => pod.linkUrl ? navigateToBannerUrl(pod.linkUrl) : nav(navPodSearchUrl(pod.filter), { state: { fresh: true } })}
                    >
                        {/* Same precedence as the admin editor: a recolorable SVG icon wins over an
                            uploaded image. The icon is a CSS-mask span (svgMaskStyle), tinted by the
                            navPodIconColor rules above; the merchant's navPodImage dict can still
                            resize or restyle it (spread after the mask, so its width/height win). */}
                        {pod.iconSvg
                            ? (
                                <span
                                    className="nav-pod-image nav-pod-icon"
                                    style={{ ...svgMaskStyle(pod.iconSvg, 64), ...homeNavPodImageStyle }}
                                />
                            )
                            : pod.imageUrl && (
                            <img
                                className="nav-pod-image"
                                src={pod.imageUrl}
                                alt={pod.name ?? ''}
                                style={{ ...homeNavPodImageStyle }}
                            />
                        )}
                        {pod.name && (
                            <span className="nav-pod-name" style={{ ...homeNavPodNameStyle }}>{pod.name}</span>
                        )}
                        {pod.linkText && (
                            <span className="nav-pod-link" style={{ ...homeNavPodLinkStyle }}>{pod.linkText}</span>
                        )}
                    </button>
                ))}
            </div>
        );
        if (!scroll) return pods;
        return (
            <div className="nav-pod-scroller-wrap">
                {navPodScroll.canScrollLeft &&
                    <Button
                        className="card-scroller-arrow card-scroller-arrow-left"
                        shape="circular"
                        appearance="subtle"
                        icon={<ChevronLeftRegular />}
                        aria-label="Scroll left"
                        style={{ background: 'rgba(255,255,255,0.9)', ...homeScrollButtonStyle }}
                        onClick={() => navPodScroll.scrollByItem('left')}
                    />
                }
                {pods}
                {navPodScroll.canScrollRight &&
                    <Button
                        className="card-scroller-arrow card-scroller-arrow-right"
                        shape="circular"
                        appearance="subtle"
                        icon={<ChevronRightRegular />}
                        aria-label="Scroll right"
                        style={{ background: 'rgba(255,255,255,0.9)', ...homeScrollButtonStyle }}
                        onClick={() => navPodScroll.scrollByItem('right')}
                    />
                }
            </div>
        );
    };

    // Affiliations / endorsements / awards band: a row of logo images with an optional label.
    // Called once for each content-flow slot ('top' — right after the banner; 'bottom' — after
    // the product sections, the default); internally renders only for the slot matching
    // homeAffiliations.position (default 'bottom'), so exactly one call produces output.
    const getAffiliations = (slot: 'top' | 'bottom') => {
        const a = homeAffiliations;
        const items = (a?.items ?? []).filter(i => !!i.imageUrl);
        if (items.length === 0) return null;
        const position = a?.position ?? 'bottom';
        if (position !== slot) return null;

        const labelPosition = (a?.label || a?.description) ? (a?.labelPosition ?? 'top') : 'none';

        // Horizontal alignment for the label/column arrangement — follows the logo row's own
        // align setting so the label reads as part of the same group.
        const groupAlignItems: React.CSSProperties['alignItems'] =
            a?.align === 'left' ? 'flex-start' :
            a?.align === 'right' ? 'flex-end' :
            a?.align === 'spaceBetween' ? 'stretch' :
            'center';

        const gapPx = a?.gap ?? 24;
        // One pod (a logo, optionally linked). In scroll mode each pod carries the gap as its own
        // right margin (rather than a flex `gap`) so two identical copies tile seamlessly under a
        // -50% translate; the duplicate copy is aria-hidden and never linked.
        const renderPods = (keyPrefix: string, opts: { marginRight?: number; ariaHide?: boolean } = {}) =>
            items.map((item) => {
                const img = (
                    <img
                        src={item.imageUrl}
                        alt={opts.ariaHide ? '' : (item.altText ?? '')}
                        aria-hidden={opts.ariaHide || undefined}
                        style={{ height: 48, width: 'auto', objectFit: 'contain', ...homeAffiliationsImageStyle }}
                    />
                );
                return (
                    <div
                        key={`${keyPrefix}-${item.id}`}
                        className="affiliations-pod"
                        aria-hidden={opts.ariaHide || undefined}
                        style={{ ...homeAffiliationsPodStyle, ...(opts.marginRight != null ? { marginRight: opts.marginRight } : {}) }}
                    >
                        {item.linkUrl && !opts.ariaHide
                            ? <a href={item.linkUrl} target="_blank" rel="noopener noreferrer">{img}</a>
                            : img}
                    </div>
                );
            });

        const logosRow = a?.scroll ? (
            // Continuous marquee: the track holds two identical copies and animates translateX(0 → -50%)
            // (see .affiliations-track keyframes in index.css). Edges fade via a mask; hover pauses it.
            <div
                className="affiliations-marquee"
                style={{
                    overflow: 'hidden',
                    width: '100%',
                    WebkitMaskImage: 'linear-gradient(to right, transparent, #000 6%, #000 94%, transparent)',
                    maskImage: 'linear-gradient(to right, transparent, #000 6%, #000 94%, transparent)',
                }}
            >
                <div
                    className="affiliations-track"
                    style={{ display: 'flex', width: 'max-content', alignItems: 'center', animationDuration: `${a?.scrollSpeed ?? 30}s` }}
                >
                    {renderPods('a', { marginRight: gapPx })}
                    {renderPods('b', { marginRight: gapPx, ariaHide: true })}
                </div>
            </div>
        ) : (
            <div
                className="affiliations-row"
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: `${gapPx}px`,
                    justifyContent: navPodJustify(a?.align) ?? 'center',
                }}
            >
                {renderPods('a')}
            </div>
        );

        // The heading block: the label (title) plus an optional description line below it. Stacked
        // and aligned to the group's alignment so a centered strip gets a centered title + blurb.
        const textAlign: React.CSSProperties['textAlign'] =
            a?.align === 'left' ? 'left' : a?.align === 'right' ? 'right' : 'center';
        const labelEl = ((a?.label || a?.description) && labelPosition !== 'none') ? (
            <div
                className="affiliations-label-block"
                style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: groupAlignItems, textAlign, maxWidth: 720 }}
            >
                {a?.label &&
                    <span className="affiliations-label" style={{ ...homeAffiliationsLabelStyle }}>{a.label}</span>
                }
                {a?.description &&
                    <span className="affiliations-description" style={{ fontSize: '1rem', lineHeight: 1.5, color: '#586575', ...homeAffiliationsDescriptionStyle }}>{a.description}</span>
                }
            </div>
        ) : null;

        let group: React.ReactNode;
        if (!labelEl) {
            group = logosRow;
        } else if (labelPosition === 'left') {
            group = (
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 16 }}>
                    {labelEl}
                    {logosRow}
                </div>
            );
        } else if (labelPosition === 'bottom') {
            group = (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: groupAlignItems, gap: 12 }}>
                    {logosRow}
                    {labelEl}
                </div>
            );
        } else {
            // 'top' — also the default when a label is present.
            group = (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: groupAlignItems, gap: 12 }}>
                    {labelEl}
                    {logosRow}
                </div>
            );
        }

        // homeBlockGap: zero this block's own outer margin — the uniform gap comes from the
        // flex-column wrapper in the return below instead (mirrors nav pods / sections above).
        const wrapped = (
            <div className="affiliations-group" style={{ margin: '1em 0', ...(homeBlockGap !== undefined ? { margin: 0 } : {}) }}>
                {group}
            </div>
        );

        if (!homeAffiliationsBandStyle) return wrapped;
        // Default vertical padding only when the merchant didn't set their own (mirrors navPodBand).
        return fullBleedBand({ padding: '28px 0', ...homeAffiliationsBandStyle }, wrapped, { key: `affiliations-band-${slot}` });
    };

    const getCategoryPods = () => {
        return (
            <Overflow overflowDirection="start">
                <div style={{ position: 'relative', margin: '1em 0', width: '100%', alignItems: 'center', ...(homeBlockGap !== undefined ? { margin: 0 } : {}) }}>
                    {homePodCss && (
                        <style>{`.category-scroll-container .category-pod-button { ${homePodCss} }`}</style>
                    )}
                    {homePodHoverCss && (
                        <style>{`.category-scroll-container .category-pod-button { transition: box-shadow .18s ease, transform .18s ease, border-color .18s ease, background-color .18s ease; } .category-scroll-container .category-pod-button:hover { ${homePodHoverCss} }`}</style>
                    )}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '12px',
                            justifyContent: 'center',
                            width: '100%',
                            ...homePodContainerStyle,
                        }}
                        className="category-scroll-container"
                    >
                        <OverflowMenu itemIds={categories.data?.map(a => a.id) ?? []} />
                        {categories.data && categories.data.map((cat) => (
                            <OverflowItem id={cat.id} key={cat.id}>
                                <button
                                    key={cat.id}
                                    className={'category-pod-button'}
                                    onClick={() => {
                                        if (cat.identifier) {
                                            nav(`/search?category=${encodeURIComponent(cat.identifier)}`);
                                        }
                                    }}
                                >
                                    {cat.smallImageUrl && (
                                        <img src={cat.smallImageUrl} alt={cat.label} style={{ width: '48px', height: '48px', objectFit: 'contain', marginBottom: '0.5em' }} />
                                    )}
                                    <span style={{ fontSize: '0.95em', textAlign: 'center', wordBreak: 'break-word', ...homePodNameStyle }}>{cat.label}</span>
                                </button>
                            </OverflowItem>
                        ))}
                    </div>
                </div>
            </Overflow>
        );
    }

    // Renders one product section — the existing collapsible header band, product grid/scroller,
    // footer, and "more like this" link. Extracted verbatim from the former sections map so its output
    // is byte-for-byte unchanged; content sections render through <ContentSection> instead. `idx` is
    // the section's render order, used for the minimize toggle and per-section full-bleed band.
    const renderProductSection = (section: IStorefrontSection, idx: number): React.ReactNode => {
        // Named-style resolution for the five section StyleProps dictionaries. Each may carry a
        // `$styleRef` pointing at a named style; the matching part is merged underneath the local
        // dict (local wins) and the reserved key is stripped so it never reaches a style prop. The
        // product dictionary references CARD-type styles, hence part 'card'. Without a ref these
        // return the very same objects, so rendering is byte-identical to before. See stylesConfig.ts.
        const resolvedStyleProps = resolveStyleProps(section.styleProps, 'band');
        const resolvedTitleStyleProps = resolveStyleProps(section.titleStyleProps, 'title');
        const resolvedHeaderStyleProps = resolveStyleProps(section.headerStyleProps, 'header');
        const resolvedFooterStyleProps = resolveStyleProps(section.footerStyleProps, 'footer');
        const resolvedProductStyleProps = resolveStyleProps(section.productStyleProps, 'card');
        const sectionBackgroundColor = resolvedProductStyleProps?.backgroundColor ?? resolvedStyleProps?.backgroundColor ?? resolvedHeaderStyleProps?.backgroundColor;
        const sectionIsDark = sectionBackgroundColor ? isDarkColor(sectionBackgroundColor) : undefined;
        // Two ways a section can't be folded: it has no header to click (hideTitle), or the merchant
        // turned collapsing off for it (collapsible === false; absent/true keeps the old behavior).
        // Either way force it expanded rather than trusting the minimize state, which is keyed on the
        // render index and can survive a config change that made this section non-collapsible.
        const canCollapse = !section.hideTitle && section.collapsible !== false;
        const isMinimized = canCollapse && minimizedSections.has(idx);
        // The minimize control sits on the section's header band, whose background is
        // merchant-configurable (titleStyleProps). Choose a glyph color that contrasts with
        // that specific color so the control stays visible on both light and dark headers.
        const headerControlBg = resolvedTitleStyleProps?.backgroundColor ?? resolvedHeaderStyleProps?.backgroundColor ?? resolvedStyleProps?.backgroundColor;
        const headerControlColor = headerControlBg ? (isDarkColor(headerControlBg) ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.7)') : 'inherit';
        const moreLikeThisParams = new URLSearchParams();
        if (section.categories && section.categories.length > 0) moreLikeThisParams.set('category', section.categories[0]);
        if (section.segments && section.segments.length > 0) moreLikeThisParams.set('segment', section.segments[0]);
        if (section.manufacturers && section.manufacturers.length > 0) moreLikeThisParams.set('manufacturer', section.manufacturers[0]);
        if (section.subCategories && section.subCategories.length > 0) moreLikeThisParams.set('subcategory', section.subCategories[0]);
        const moreLikeThisUrl = moreLikeThisParams.size > 0 ? `/search?${moreLikeThisParams.toString()}` : null;
        // Container style with a phone adaptation: thin a wide LCARS rail (and shrink the
        // elbow / drop the header gap) so a single full-width card isn't crowded against
        // the border. Desktop rendering is unchanged.
        const filteredSectionStyle = Object.fromEntries(Object.entries(resolvedStyleProps ?? {}).filter(([, v]) => v !== undefined && v !== ''));
        const hasRail = !!(filteredSectionStyle.borderLeft || filteredSectionStyle.borderLeftWidth);
        const containerStyleProps = isNarrow && hasRail
            ? { ...filteredSectionStyle, borderLeftWidth: '12px', borderTopLeftRadius: '14px' }
            : filteredSectionStyle;
        // A section title with no left inset of its own ends up hard against the content column's
        // clipping edge whenever that column runs to the viewport edge, and its first glyph shaves —
        // worst with large display faces, which sit tight to their box. That happens on phones (the
        // edge-to-edge rule strips #content-inner's 1em gutter) AND, for a fullWidthLayout store, at
        // every width below its maxWidth cap — so this can't be gated on narrow alone. Guarantee a
        // small inset in those cases, but ONLY when the merchant set none, so a deliberate indent is
        // never overridden. Tested against the RESOLVED props, since a named style or palette token
        // can be what supplies the inset.
        const contentRunsToEdge = isNarrow || homeConfig.fullWidthLayout === true;
        const needsEdgeInset = contentRunsToEdge && hasZeroLeftInset(resolvedTitleStyleProps);
        const titleStyleProps = isNarrow || needsEdgeInset
            ? {
                ...resolvedTitleStyleProps,
                ...(isNarrow ? { marginLeft: 0 } : {}),
                ...(needsEdgeInset ? { paddingLeft: EDGE_MIN_LEFT_INSET } : {}),
            }
            : resolvedTitleStyleProps;
        const productDisplayStyle = isNarrow ? { ...resolvedProductStyleProps, padding: '12px 8px 16px' } : resolvedProductStyleProps;

        // Every page-level layer, as THIS section sees it (AC-1165). The page value with the section's own
        // override merged over it; a section that overrides nothing gets the page object back unchanged and
        // identical, so a storefront that has never set one renders exactly as it did before — and the
        // memoised page styles above keep their referential equality for it.
        const ov = section.styleOverrides;
        const sectionCardStyle = resolveSectionOverride(homeCardStyle, ov, 'card');
        const sectionCardHoverStyle = resolveSectionOverride(homeCardHoverStyle, ov, 'cardHover');
        const sectionNameStyle = resolveSectionOverride(homeNameStyle, ov, 'title');
        const sectionPriceStyle = resolveSectionOverride(homePriceStyle, ov, 'price');
        const sectionImageStyle = resolveSectionOverride(homeImageStyle, ov, 'image');
        const sectionAddToCartStyle = resolveSectionOverride(homeAddToCartStyle, ov, 'addToCartButton');
        const sectionScrollButtonStyle = resolveSectionOverride(homeScrollButtonStyle, ov, 'scrollButton');
        const sectionScrollbarStyle = resolveSectionOverride(homeScrollbarStyle, ov, 'scrollbar');
        const sectionMoreLikeThisStyle = resolveSectionOverride(homeMoreLikeThisStyle, ov, 'moreLikeThisButton');
        // The knocked-out block behind the title is the one layer that CANNOT be an inline style: the
        // element it targets is rendered deep inside the section markup, which is why the page-wide version
        // is a scoped rule. So this section gets a class of its own and one rule under it — emitted only
        // when the section actually overrides the layer, so no storefront gains a stylesheet rule it had
        // no reason to.
        const titleTextOverride = resolveSectionOverride(
            homeConfig.sectionTitleText, ov, 'sectionTitleText');
        const overridesTitleText = !!ov?.sectionTitleText && Object.keys(ov.sectionTitleText).length > 0;
        const sectionScopeClass = overridesTitleText ? `sf-section-ov-${idx}` : undefined;
        const titleTextCss = overridesTitleText && titleTextOverride
            ? importantStyleCss(elementStyleFrom(titleTextOverride))
            : undefined;
        // A section in a full-bleed band renders flush (marginBottom 0) so adjacent
        // bands stripe continuously; the band's own padding supplies vertical spacing.
        // Precedence: fullWidthBackground gets its band from SectionRowLayout (row-level), so it must
        // NOT also get the legacy per-index band wrap here — the new flag wins for this section.
        const sectionBandStyle = section.fullWidthBackground ? undefined : homeSectionBandStyles[idx];
        // Flush when in any band (legacy per-index OR the new row-level full-width band).
        const bandFlush = !!sectionBandStyle || !!section.fullWidthBackground;
        const sectionEl = (
            <div key={`${idx}-section-display`} className={sectionScopeClass} style={{position: 'relative', padding: '1em', marginBottom: bandFlush ? 0 : '1em', ...resolvedHeaderStyleProps, ...containerStyleProps}}>
                {/* Scoped to this section's own class, so it overrides the page-wide rule for this section
                    and leaves every other section on the page alone. */}
                {titleTextCss && sectionScopeClass && (
                    <style>{`.${sectionScopeClass} .section-header .section-title-text { ${titleTextCss} }`}</style>
                )}
                {/* Header Area — omitted entirely when hideTitle: no title band, no collapse control,
                    no aria-expanded wiring. The section then always renders expanded (see isMinimized). */}
                {!section.hideTitle &&
                <div
                    className={'section-header'}
                    // A non-collapsible header is not a control: no pointer cursor, no button role,
                    // not in the tab order, no aria-expanded, and no handlers. Announcing a button
                    // that does nothing is worse for a screen-reader user than plain text.
                    style={{position: 'relative', ...(canCollapse ? { cursor: 'pointer' } : {})}}
                    role={canCollapse ? 'button' : undefined}
                    tabIndex={canCollapse ? 0 : undefined}
                    aria-expanded={canCollapse ? !isMinimized : undefined}
                    aria-label={canCollapse ? (isMinimized ? 'Expand section' : 'Collapse section') : undefined}
                    onClick={canCollapse ? () => toggleSection(idx) : undefined}
                    onKeyDown={canCollapse ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleSection(idx);
                        }
                    } : undefined}
                >
                    {/* Passive +/- indicator — the whole header bar is the toggle now (a much
                        bigger tap target on mobile). pointer-events:none lets taps fall through.
                        Omitted when the section can't collapse: there is no state to indicate. */}
                    {canCollapse && <span
                        aria-hidden='true'
                        className='section-toggle'
                        style={{
                            position: 'absolute',
                            // Center the indicator on the header band in both states.
                            top: '50%',
                            transform: 'translateY(-50%)',
                            right: 20,
                            width: 24,
                            height: 24,
                            // Luminance-aware: contrast against the header band's own color so the
                            // indicator stays legible on light (gold, sky) and dark headers alike.
                            color: headerControlColor,
                            opacity: 0.65,
                            fontSize: 20,
                            lineHeight: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                            // Merchant overrides last, so any of the above (including the
                            // luminance-derived color and the right/top placement) can be replaced.
                            ...homeSectionToggleStyle,
                        }}
                    >
                        {isMinimized ? sectionExpandIcon : sectionCollapseIcon}
                    </span>}
                    <div className='section-header-inner'>
                        <div style={{...titleStyleProps}}>
                            {/* The title TEXT, separate from the band it sits on so a merchant can
                                style the two independently (homePageConfig.sectionTitleText) — e.g.
                                a knocked-out block behind the words, full band height, with the text
                                itself tinted to the band colour. */}
                            <div className='section-title-text'>{section.title}</div>
                            {section.subtitle && <div style={{fontSize: '0.65em', opacity: 0.85, fontWeight: 'normal'}}>{section.subtitle}</div>}
                        </div>
                        {section.headerCells && section.headerCells.length > 0 &&
                            <div className='section-header-cells' style={{display: 'flex'}}>
                                {renderSectionCells(section.headerCells)}
                            </div>
                        }
                    </div>
                </div>}
                {!isMinimized && <>
                    {/* Product Area */}
                    <div className={'section-featured-product-display'} style={{...productDisplayStyle}}>
                        {/* onDismiss only for a dismissable section ("Recommended For You"): it is what
                            makes ProductCard render the ✕ at all, so every other section stays as-is. */}
                        <FeaturedProductDisplay products={section.products ?? []} isDark={sectionIsDark} showFeaturedFlag={false} scroll={section.scrollProducts} scrollButtonStyle={sectionScrollButtonStyle} scrollbarStyle={sectionScrollbarStyle} cardStyle={sectionCardStyle} cardHoverStyle={sectionCardHoverStyle} nameStyle={sectionNameStyle} priceStyle={sectionPriceStyle} imageStyle={sectionImageStyle} addToCartStyle={sectionAddToCartStyle} onDismiss={section.dismissable ? dismissRecommendedProduct : undefined} />
                    </div>
                    {moreLikeThisUrl &&
                        <div style={{display: 'flex', justifyContent: 'flex-end', paddingRight: 20, paddingBottom: 4}}>
                            <button
                                className='more-like-this-button'
                                style={sectionMoreLikeThisStyle}
                                onClick={() => nav(moreLikeThisUrl)}
                            >
                                {L('localization_home_more_like_this')}
                            </button>
                        </div>
                    }
                    {/* Footer Area — LAST in the section, after the "more like this" link. A styled
                        footer band is the section's closing rule (merchants full-bleed it as a
                        separator), and a link rendered below that rule reads as belonging to the NEXT
                        section. Sections with footer CELLS are unaffected in content, only in order. */}
                    <div className={'section-featured-product-footer'} style={{...resolvedFooterStyleProps}}>
                        <div className={'section-featured-product-footer-inner'}>
                            {section.footerCells && section.footerCells.length > 0 &&
                                <div className='section-footer-cells'>
                                    {renderSectionCells(section.footerCells)}
                                </div>
                            }
                        </div>
                    </div>
                </>}
            </div>
        );
        return sectionBandStyle ? fullBleedBand(sectionBandStyle, sectionEl, { key: `${idx}-section-band` }) : sectionEl;
    };

    // Renders the ordered section list (product + content sections) through SectionRowLayout, which
    // packs fractional-width sections into rows and leaves full-width ones standalone (byte-identical
    // to the former plain map when every section is full-width). Content sections dispatch to
    // <ContentSection>; product sections keep the renderer above. Sections arrive already
    // page-filtered from the API.
    const renderSections = (sections: IStorefrontSection[]) => (
        <SectionRowLayout
            sections={sections}
            renderSection={(section, idx) => isContentSection(section)
                ? <ContentSection key={`${idx}-content`} section={section} contentMaxWidth={settings.data?.maxWidth} storefrontBackgroundColor={settings.data?.backgroundColor} />
                : renderProductSection(section, idx)}
            // A fractional row (or a flagged full section) whose members opt into fullWidthBackground
            // gets one edge-to-edge band, colored by the first flagged member. Reuse fullBleedBand so
            // the band spans 100vw while its inner content re-centers at the page max-width column.
            renderBand={(backgroundColor, children, key) =>
                fullBleedBand({ backgroundColor, padding: '28px 0' }, children, { key })}
        />
    );

    const BannerSkeleton = () => {
        const bannerHeight = effectiveBannerHeight ? `${effectiveBannerHeight}px` : '350px';
        return (
            <div className="skeleton-box" style={{
                width: '100%', height: bannerHeight, minHeight: bannerHeight, borderRadius: 8,
            }} />
        );
    };

    const CategoryPodsSkeleton = () => (
        <div style={{display: 'flex', flexDirection: 'row', gap: '1em', justifyContent: 'center', margin: '1em 0', flexWrap: 'wrap'}}>
            {Array.from({length: 6}).map((_, i) => (
                <div key={i} style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8}}>
                    <div className="skeleton-box" style={{width: 100, height: 100, borderRadius: 12}}/>
                </div>
            ))}
        </div>
    );

    const ProductCardsSkeleton = () => {
        // Reuse the same responsive card-container/card classes as the real grid so the
        // skeleton reflows and stays centered identically at every viewport width
        // (2-up below 700px, fixed 250px above) instead of left-aligning fixed-width cards.
        //
        // The count is MEASURED rather than fixed. A hard count only looks right at the one width
        // where it happens to divide evenly: at 6 cards and five per row you get a full row plus a
        // lone orphan below it, which reads as a broken grid rather than as loading. Filling exactly
        // one row keeps the placeholder honest at every width.
        //
        // Measuring beats clipping a fixed count here because `.card`'s min-height changes across
        // breakpoints (240-300px), so there is no single row height to clip to.
        const ref = React.useRef<HTMLDivElement>(null);
        const [cardCount, setCardCount] = React.useState(5);
        React.useLayoutEffect(() => {
            const el = ref.current;
            if (!el) return;
            const measure = () => {
                const card = el.firstElementChild as HTMLElement | null;
                const cardWidth = card?.getBoundingClientRect().width ?? 0;
                if (cardWidth <= 0) return;
                const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
                // Card widths are independent of how many we render (fixed 250px, or ~50% when
                // 2-up), so this converges immediately instead of oscillating with the count.
                setCardCount(Math.max(1, Math.floor((el.clientWidth + gap) / (cardWidth + gap))));
            };
            measure();
            const observer = new ResizeObserver(measure);
            observer.observe(el);
            return () => observer.disconnect();
        }, []);
        return (
            <div ref={ref} className="card-container" style={{paddingTop: '1em'}}>
                {Array.from({length: cardCount}).map((_, i) => (
                    <div key={i} className="card">
                        <div className="skeleton-box" style={{width: '100%', height: '100%', minHeight: 276, borderRadius: 8}}/>
                    </div>
                ))}
            </div>
        );
    };

    const isBannerLoading = settings.isLoading || settings.isFetching;
    const areCategoriesLoading = categories.isLoading || categories.isFetching;
    const areProductsLoading = featuredProducts.isLoading || featuredProducts.isFetching || bundles.isLoading || bundles.isFetching;

    // A /:pageSlug that doesn't resolve to an enabled custom page is a 404 — render the same
    // NotFound the router's '*' route uses, so a bad single-segment slug behaves like any other
    // unknown path. (Multi-segment unknown paths never reach here; they hit the '*' route directly.)
    if (isUnknownPage) {
        return <NotFoundPage />;
    }

    // Custom page: render ONLY the page's banners + sections. None of the main-homepage extras (store
    // title, nav pods, affiliations, category pods, search bar, bannerText, bundles row, browse-all).
    // The banner set and sections are already scoped to this page (validBanners effect + featured
    // `page` arg). document.title is handled by the effect above. This branch keeps the main homepage
    // return below byte-identical when no slug is present.
    if (customPage) {
        return (
            <div style={{ width: '100%', maxWidth: homeMaxWidth, margin: '0 auto', padding: 0, boxSizing: 'border-box' }}>
                {isBannerLoading
                    ? (homeBannerFullWidth ? fullBleedBand(undefined, <BannerSkeleton />, { constrainInner: false }) : <BannerSkeleton />)
                    : (homeBannerFullWidth ? fullBleedBand(undefined, getBanner(), { constrainInner: false }) : getBanner())}
                {areProductsLoading
                    ? <div><ProductCardsSkeleton /><ProductCardsSkeleton /><ProductCardsSkeleton /></div>
                    : ((featuredProducts.data?.sections && featuredProducts.data.sections.length > 0)
                        ? <div style={{ paddingTop: '1em' }}>{renderSections(featuredProducts.data.sections)}</div>
                        : null)}
            </div>
        );
    }

    return (
        // No overflowX:hidden here — it would clip the full-bleed bands (fullBleedBand's 100vw
        // breakout). Horizontal overflow is already clipped by the layout ancestors (#content /
        // html / body / #root use overflow-x: clip — see Layout.tsx), so nothing scrolls sideways.
        <div style={{ width: '100%', maxWidth: homeMaxWidth, margin: '0 auto', padding: 0, boxSizing: 'border-box', ...(homeContentPaddingTop !== undefined ? { marginTop: homeContentPaddingTop } : {}), ...(underHeaderActive ? { marginTop: 0 } : {}) }}>
            {homeCardHoverCss && (
                <style>{`.card { transition: box-shadow .18s ease, transform .18s ease, border-color .18s ease, background-color .18s ease; } .card:hover { ${homeCardHoverCss} }`}</style>
            )}
            {/* Section collapse control hover. Keyed off the HEADER's hover, not the indicator's: the
                whole band is the toggle, so the indicator has to react when the pointer is anywhere
                on it — and it is pointer-events:none, so it would never see a hover of its own. */}
            {homeSectionTitleTextCss && (
                <style>{`.section-header .section-title-text { ${homeSectionTitleTextCss} }`}</style>
            )}
            {homeSectionToggleHoverCss && (
                <style>{`.section-header .section-toggle { transition: color .18s ease, opacity .18s ease, background-color .18s ease, transform .18s ease; } .section-header:hover .section-toggle { ${homeSectionToggleHoverCss} }`}</style>
            )}
            {/* Button hover from a referenced button style's `hover` part — the floating add-to-cart
                button on product cards (`.sf-card-add-to-cart`) and the rows-view button. Only
                emitted when a preset actually authors a hover part. */}
            {homeAddToCartHoverCss && (
                <style>{`.sf-card-add-to-cart, .add-to-cart-button { transition: box-shadow .18s ease, transform .18s ease, border-color .18s ease, background-color .18s ease; } .sf-card-add-to-cart:hover, .add-to-cart-button:hover { ${homeAddToCartHoverCss} }`}</style>
            )}
            {/* On phones the title wraps; a merchant's tight single-line line-height (e.g. 20px on a
                28px font) then overlaps, so ensure a readable wrapped line-height on narrow screens.
                homeContentPaddingTop: collapse the h1's own top/bottom browser-default margins so the
                configured marginTop above (applied on the outer container below — can be negative to
                pull the block up into the navbar's own clearance) is the ONLY header→banner gap,
                whether or not a title is set (an empty title still renders the h1, and its margins
                would otherwise still apply). underHeaderActive: the hero must be the first thing under
                the transparent header, so the title moves below the banner (inside the block wrapper —
                see there); skipped when empty so no phantom h1 margins/gap slot appear. */}
            {!underHeaderActive &&
            <h1 className='home-title' style={{ textAlign: 'center', ...homePageTitleStyle, ...(isNarrow ? { lineHeight: 1.3 } : {}), ...(homeContentPaddingTop !== undefined ? { marginTop: 0, marginBottom: 0 } : {}) }}>{settings.data?.title}</h1>
            }
            {/* homeBlockGap: when set, this wrapper switches to a flex column with a uniform rowGap
                across every block below (banner → sections), and each block's own margin/padding
                that would otherwise add to that gap is zeroed at its source (see the ...homeBlockGap
                overrides throughout). When unset this is a bare, unstyled div — inert in normal flow,
                so today's block layout (plain stacked margins/padding) is byte-for-byte unchanged. */}
            <div style={homeBlockGap !== undefined ? { display: 'flex', flexDirection: 'column', rowGap: homeBlockGap } : undefined}>
            {isBannerLoading ? (homeBannerFullWidth ? fullBleedBand(undefined, <BannerSkeleton />, { constrainInner: false }) : <BannerSkeleton />) : (homeBannerFullWidth ? fullBleedBand(undefined, getBanner(), { constrainInner: false }) : getBanner())}
            {underHeaderActive && !!settings.data?.title?.trim() &&
            <h1 className='home-title' style={{ textAlign: 'center', ...homePageTitleStyle, ...(isNarrow ? { lineHeight: 1.3 } : {}), ...(homeBlockGap !== undefined ? { marginTop: 0, marginBottom: 0 } : {}) }}>{settings.data.title}</h1>
            }
            {getAffiliations('top')}
            {settings.data?.bannerText &&
            <div className={'home-message'} style={{marginTop: '1em', ...(homeBlockGap !== undefined ? { marginTop: 0 } : {}), ...homeBannerTextStyle}}>
                <p>{settings.data.bannerText}</p>
            </div>
            }
            {(() => {
                const navPodsEl = getNavPods();
                if (!navPodsEl) return null;
                return homeNavPodBandStyle ? fullBleedBand(homeNavPodBandStyle, navPodsEl) : navPodsEl;
            })()}
            {settings.data?.useCategories && (areCategoriesLoading ? <CategoryPodsSkeleton /> : getCategoryPods())}

            {!settings.data?.useHeaderSearch &&
                <div style={{margin: '1em', ...(homeBlockGap !== undefined ? { margin: 0 } : {})}} className='home-search-bar'>
                    <SearchBox
                        style={{ margin: '0 auto', width: '100%' }}
                        placeholder={L('localization_search_placeholder')}
                        value={keywords}
                        onValueChange={setKeywords}
                        suggestionsAvailable={settings.data?.enableSearchAutocomplete}
                        autocomplete={homeAutocomplete}
                    />
                </div>
            }
            {areProductsLoading ? <div><ProductCardsSkeleton /><ProductCardsSkeleton /><ProductCardsSkeleton /></div> : (
                (featuredProducts.data?.sections && featuredProducts.data.sections.length > 0) ?
                <div style={{paddingTop: '1em', ...(homeBlockGap !== undefined ? { paddingTop: 0 } : {})}}>
                    {renderSections(featuredProducts.data.sections)}
                </div> :
                <div>
                    <FeaturedProductDisplay products={featuredProducts.data?.products ?? []} cardStyle={homeCardStyle} cardHoverStyle={homeCardHoverStyle} nameStyle={homeNameStyle} priceStyle={homePriceStyle} imageStyle={homeImageStyle} addToCartStyle={homeAddToCartStyle} />
                    <FeaturedBundlesDisplay bundles={bundles.data ?? []} showAddToCart />
                </div>
            )}
            {getAffiliations('bottom')}
            </div>
            {(() => {
                // AC-969 — the bottom "Browse All" area becomes an optional full-bleed CTA band
                // (heading + subheading + the styled button). Absent config = the plain button, unchanged.
                const cta = homeConfig.browseAllBand;
                const button = (
                    <button type="button" className="browse-all-button" style={cta ? elementStyleFrom(cta.buttonStyle) : undefined} onClick={showAll}>
                        {L('localization_home_browse_all')}
                    </button>
                );
                if (!cta) {
                    return <div style={{ paddingTop: '2em', display: 'flex', justifyContent: 'center' }}>{button}</div>;
                }
                // Compose the band background from the discrete config fields: a solid color, an image,
                // and an optional tint wash over the image (opacity), with an optional fixed attachment.
                const toRgba = (c: string, a: number): string => {
                    const h = c.replace('#', ''); const n = h.length === 3 ? h.split('').map(x => x + x).join('') : h;
                    if (n.length < 6) return c; // already rgb()/rgba()/named — use as-is
                    return `rgba(${parseInt(n.slice(0, 2), 16)}, ${parseInt(n.slice(2, 4), 16)}, ${parseInt(n.slice(4, 6), 16)}, ${a})`;
                };
                const layers: string[] = [];
                if (cta.backgroundImageUrl && cta.overlayColor) { const wash = toRgba(cta.overlayColor, (cta.overlayOpacity ?? 50) / 100); layers.push(`linear-gradient(${wash}, ${wash})`); }
                if (cta.backgroundImageUrl) layers.push(`url("${cta.backgroundImageUrl}")`);
                const bandStyle: React.CSSProperties = {
                    minHeight: cta.minHeight ?? 320,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: cta.backgroundColor,
                    backgroundImage: layers.length ? layers.join(', ') : undefined,
                    backgroundSize: cta.backgroundImageUrl ? 'cover' : undefined,
                    backgroundPosition: cta.backgroundImageUrl ? 'center' : undefined,
                    backgroundRepeat: cta.backgroundImageUrl ? 'no-repeat' : undefined,
                    backgroundAttachment: cta.fixed ? 'fixed' : undefined,
                };
                // textStyleFrom emits `fontSize: undefined` when unset, which would clobber the
                // default via the spread — so apply the size AFTER the spread with a fallback.
                const hs = textStyleFrom(cta.headingStyle, 'heading');
                const ss = textStyleFrom(cta.subheadingStyle, 'body');
                const inner = (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: '0.9em', padding: '56px 24px', maxWidth: 860, margin: '0 auto' }}>
                        {cta.heading && <div style={{ fontWeight: 700, lineHeight: 1.12, ...hs, fontSize: hs.fontSize ?? 'clamp(2rem, 4.5vw, 3rem)' }}>{cta.heading}</div>}
                        {cta.subheading && <div style={{ lineHeight: 1.55, ...ss, fontSize: ss.fontSize ?? 'clamp(1rem, 2vw, 1.2rem)' }}>{cta.subheading}</div>}
                        {button}
                    </div>
                );
                // The band is the last thing on the page — drop #content's bottom padding so it sits
                // flush against the footer (only while a band is configured; other pages keep the gap).
                return (
                    <>
                        <style>{'#content { padding-bottom: 0 !important; }'}</style>
                        {fullBleedBand(bandStyle, inner, { key: 'browse-all-band' })}
                    </>
                );
            })()}
        </div>
    );
}

export default HomePage;
